import { Injectable } from '@angular/core';

import {
  Balance,
  BroadcastedTx,
  ChainApi1stCall,
  Provider,
  SignedTx,
  Signer,
  SignerInvokeTx,
  UserData,
} from '@waves/signer';
import { ProviderWeb } from '@waves.exchange/provider-web';
import { ProviderCloud } from '@waves.exchange/provider-cloud';
import { ProviderKeeperMobile } from '@keeper-wallet/provider-keeper-mobile';
import ProviderLedger from '@waves/provider-ledger';
import { WavesNodeConnectorService } from '@services/waves-node-connector.service';
import BN from '@utils/bn';
import {
  BehaviorSubject,
  combineLatest,
  interval,
  map,
  merge,
  mergeMap,
  Observable,
  of,
  Subject,
  switchMap,
  zip,
} from 'rxjs';
import { ProviderKeeper } from '@waves/provider-keeper';
import { broadcast, waitForTx } from '@waves/waves-transactions';
import { Store } from '@ngrx/store';
import * as plutoActions from '@stores/pluto.actions';

// It's a magic enum, provided by waves docs
// eslint-disable-next-line @typescript-eslint/naming-convention
export enum LOGIN_TYPE {
  SIGNER_SEED = 'SIGNER_SEED',
  SIGNER_EMAIL = 'SIGNER_EMAIL',
  KEEPER = 'KEEPER',
  KEEPER_MOBILE = 'KEEPER_MOBILE',
  LEDGER = 'LEDGER',
}

export interface IInvokeTxParams {
  fee?: number;
  dApp: string;
  payment: Array<{ assetId: string | null; amount: string | number }>;
  call: {
    function: string;
    args: Array<{ type: 'integer' | 'string'; value: string }>;
  };
}

@Injectable({
  providedIn: 'root',
})
export class WavesConnectorService {
  private NODE_URL = 'https://nodes.wx.network';

  constructor(
    private _wavesNodeConnectorService: WavesNodeConnectorService,
    private _store: Store,
  ) {}

  private _loginType?: LOGIN_TYPE;

  public get loginType() {
    return this._loginType;
  }

  public set loginType(loginType: LOGIN_TYPE | undefined) {
    this._loginType = loginType;
  }

  private _signer$: BehaviorSubject<Signer | null> =
    new BehaviorSubject<Signer | null>(null);

  public signer$: Observable<Signer | null> = this._signer$.asObservable();

  private _loginData$: BehaviorSubject<UserData | undefined> =
    new BehaviorSubject<UserData | undefined>(undefined);

  public updateLoginData(data: UserData | undefined) {
    if (!data || this._loginData$.value === data) {
      return;
    }
    this._loginData$.next(data);
  }

  protected loginData$: Observable<UserData | undefined> =
    this._loginData$.asObservable();

  public address$: Observable<string | undefined> = this.loginData$.pipe(
    map<UserData | undefined, string | undefined>((data) => {
      return data?.address;
    }),
  );

  public publicKey$: Observable<string | undefined> = this.loginData$.pipe(
    map<UserData | undefined, string | undefined>((data) => data?.publicKey),
  );

  private _getProviderFromLoginType(loginType: LOGIN_TYPE): Provider {
    switch (loginType) {
      case LOGIN_TYPE.SIGNER_SEED:
        return new ProviderWeb();
      case LOGIN_TYPE.SIGNER_EMAIL:
        return new ProviderCloud();
      case LOGIN_TYPE.KEEPER:
        return new ProviderKeeper();
      case LOGIN_TYPE.KEEPER_MOBILE:
        return new ProviderKeeperMobile();
      case LOGIN_TYPE.LEDGER:
        return new ProviderLedger();
      default:
        throw new Error('Unfamiliar login type:', loginType);
    }
  }

  private _createSigner(loginType: LOGIN_TYPE): Signer {
    const provider: Provider = this._getProviderFromLoginType(loginType);
    const signer = new Signer();

    signer.setProvider(provider);
    return signer;
  }

  public login(
    loginType: LOGIN_TYPE,
  ): Observable<{ signer: Signer; loginData: UserData }> {
    console.trace();

    const subject$: Subject<{
      signer: Signer;
      loginData: UserData;
    }> = new Subject<{
      signer: Signer;
      loginData: UserData;
    }>();

    this._loginType = loginType;

    const signer = this._createSigner(loginType);

    signer.login().then((loginData: UserData) => {
      Object.freeze(signer);
      
      this._loginData$.next(loginData);
      
      this._signer$.next(signer);
      
      const signerWithLoginData: { signer: Signer; loginData: UserData } = {
        signer,
        loginData,
      };
      
      subject$.next(signerWithLoginData);
      subject$.complete();

      for (const action of this.stackToCallAfterLogin) {
        if (action) {
          action();
        }
      }
      this.stackToCallAfterLogin = [];
    });

    return subject$.asObservable();
  }

  public logout() {
    this._loginType = undefined;
    this._loginData$.next(undefined);
    this._signer$.next(null);
    return of({});
  }

  public balance$: Observable<Balance[]> = this.signer$.pipe(
    mergeMap(async (signer$) => {
      return signer$ ? await signer$.getBalance() : [];
    }),
  );

  private _updateBalancesSubject: Subject<void> = new Subject();

  public updateBalances() {
    this._updateBalancesSubject.next();
  }

  public getAssetBalance$(assetId: string): Observable<BN> {
    return merge(
      of(0),
      this._updateBalancesSubject.asObservable(),
      interval(2*1e3),
    ).pipe(
      mergeMap(() => this.address$),
      switchMap((address) => {
        return address
          ? this._wavesNodeConnectorService.getAssetBalanceByAddress$(
              address,
              assetId,
            )
          : of(BN.ZERO);
      }),
      map((balance) => BN.formatUnits(balance, 8)),
    );
  }

  public getStakedBalance$(dAppAddress: string): Observable<BN> {
    return merge(
      of(''),
      this._updateBalancesSubject.asObservable().pipe(map(() => '')),
      interval(2*1e3).pipe(map(() => '')),
    ).pipe(
      mergeMap(() => this.address$),
      switchMap((address: string | undefined) => {
        return address
          ? this._wavesNodeConnectorService.getStakedAmountByAddress$(
              dAppAddress,
              address,
            )
          : of(BN.ZERO);
      }),
      map((balance) => BN.formatUnits(balance, 8)),
    );
  }

  /**
   * Logs in if not enough data to perform an action
   * @returns {boolean} isLoggedIn
   */
  private _loginIfNeeded(): boolean {
    if (!this._signer$.getValue()) {
      this._store.dispatch(plutoActions.showLoginModal());
      return false;
    }
    return true;
  }

  public async invoke(
    txParams: IInvokeTxParams,
    onTransactionSent?: Function,
    recall = true,
  ): Promise<string | void> {
    const isLoggedIn = this._loginIfNeeded();
    if (!isLoggedIn) {
      if (recall)
        this.stackToCallAfterLogin.push(() =>
          this.invoke(txParams, onTransactionSent, false),
        );
      return;
    }

    const transaction: ChainApi1stCall<SignerInvokeTx> = await this._signer$
      .getValue()!
      .invoke({
        ...txParams,
        fee: txParams.fee ?? 900000,
      });

    const broadcasted: BroadcastedTx<SignedTx<[SignerInvokeTx]>> =
      await transaction.broadcast();

    //FIXME: txId is undefined
    const txId = broadcasted[0].id;

    if (onTransactionSent) onTransactionSent();

    await waitForTx(txId, {
      apiBase: this.NODE_URL,
    });

    return txId;
  }

  public isLoggedIn$: Observable<boolean> = this.address$.pipe(
    map((val) => !!val),
  );

  public stackToCallAfterLogin: Function[] = [];
}
